import { useMemo } from 'react'
import { CHART_COLORS, CHART_PALETTE } from '@/lib/chart-theme'
import { StatCard, InfoTooltip } from './shared'

interface EstimatorData {
  estimatorId: string
  estimatorName: string
  totalEstimates: number
  rejections: number
  rejectionRate: number
}

interface RejectionRateReportData {
  summary: {
    totalRejections: number
    totalEstimates: number
    overallRate: number
  }
  chartData: {
    byEstimator: EstimatorData[]
  }
}

interface RejectionRateReportProps {
  data: RejectionRateReportData
}

function MiniDonutRing({
  name,
  rate,
  color,
}: {
  name: string
  rate: number
  color: string
}) {
  const r = 48
  const strokeW = 10
  const cx = 56
  const cy = 56
  const circ = 2 * Math.PI * r
  const dashOffset = circ * (1 - rate / 100)

  return (
    <div className="rep-mini-donut">
      <svg width="112" height="112" viewBox="0 0 112 112">
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="#E2E8F0"
          strokeWidth={strokeW}
        />
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={strokeW}
          strokeDasharray={circ}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform="rotate(-90 56 56)"
        />
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          fill="var(--s800)"
          fontSize="16"
          fontWeight="700"
        >
          {rate}%
        </text>
      </svg>
      <span className="rep-mini-donut-name">{name}</span>
    </div>
  )
}

export function RejectionRateReport({
  data,
}: RejectionRateReportProps) {
  const { summary, chartData } = data

  const table = useMemo(
    () =>
      (chartData?.byEstimator || []).sort(
        (a, b) => b.rejectionRate - a.rejectionRate
      ),
    [chartData]
  )

  return (
    <div className="rep-report">
      <div className="rep-stats-row">
        <StatCard title="Total Rejections" value={summary?.totalRejections ?? 0} color={CHART_COLORS.coral} />
        <StatCard title="Total Estimates" value={summary?.totalEstimates ?? 0} color={CHART_COLORS.blue} />
        <StatCard title="Overall Rate" value={`${summary?.overallRate ?? 0}%`} color={CHART_COLORS.navy} />
      </div>

      <div className="rep-chart-card">
        <InfoTooltip text="Individual donut rings for each estimator make rejection rates instantly scannable — the filled arc is more intuitive than comparing bar heights for percentages." />
        <div className="rep-mini-donut-grid">
          {table.map((est, idx) => (
            <MiniDonutRing
              key={est.estimatorId}
              name={est.estimatorName}
              rate={Math.round(est.rejectionRate)}
              color={CHART_PALETTE[idx % CHART_PALETTE.length]}
            />
          ))}
        </div>

        {table.length === 0 && (
          <div className="rep-loading">No data available</div>
        )}
      </div>

      {table.length > 0 && (
        <div className="rep-chart-card">
          <h4 className="rep-table-title">Detailed Breakdown</h4>
          <table className="rep-table">
            <thead>
              <tr>
                <th>Estimator</th>
                <th className="ta-right">Total Estimates</th>
                <th className="ta-right">Rejections</th>
                <th className="ta-right">Rejection Rate</th>
              </tr>
            </thead>
            <tbody>
              {table.map((est) => (
                <tr key={est.estimatorId}>
                  <td>{est.estimatorName}</td>
                  <td className="ta-right">{est.totalEstimates}</td>
                  <td className="ta-right">{est.rejections}</td>
                  <td className="ta-right">
                    <span className="rep-badge-pct">
                      {Math.round(est.rejectionRate)}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
